import React, { useState } from 'react';
import { 
  Coins, 
  PieChart, 
  Shield, 
  Users, 
  Zap, 
  Star, 
  Trophy, 
  Crown, 
  CheckCircle,
  ArrowRight,
  Copy,
  TrendingUp,
  Lock,
  Globe,
  Wallet,
  DollarSign,
  Layers,
  Database,
  Timer,
  Award,
  Target
} from 'lucide-react';

interface TokenomicsProps {
  onLaunchPlatform?: () => void;
  onNavigateToPage?: (page: string) => void;
}

export default function TokenomicsPage({ onLaunchPlatform, onNavigateToPage }: TokenomicsProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const [copiedContract, setCopiedContract] = useState(false);

  // EKKO Logo URL
  const EKKO_LOGO = "https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/EkkoLogo3%20(2).png";
  const CONTRACT_ADDRESS = "89S9RdgynPq5odSRmcCDAzg26iYuRw4wqUmzMbjUpump";

  const copyContract = () => {
    navigator.clipboard.writeText(CONTRACT_ADDRESS);
    setCopiedContract(true);
    setTimeout(() => setCopiedContract(false), 2000);
  };

  const tokenDistribution = [
    { label: 'Community Ownership', percentage: 96.5, color: 'from-[#24c35b] to-[#1eaa4e]', icon: <Users className="w-5 h-5" /> },
    { label: 'Development', percentage: 2, color: 'from-[#24c35b] to-[#1eaa4e]', icon: <Shield className="w-5 h-5" /> },
    { label: 'Marketing', percentage: 1, color: 'from-[#24c35b] to-[#1eaa4e]', icon: <TrendingUp className="w-5 h-5" /> },
    { label: 'Team', percentage: 0.5, color: 'from-[#24c35b] to-[#1eaa4e]', icon: <Award className="w-5 h-5" /> }
  ];

  const accessTiers = [
    {
      name: 'TIER 1',
      amount: '25,000',
      color: 'from-[#24c35b] to-[#1eaa4e]',
      icon: <Target className="w-8 h-8" />,
      features: [
        'Rug Tracker',
        'Whale Tracker', 
        'Custom Filters',
        'Auto Trader',
        'Jupiter Swap (built-in)',
        'MEV Protection (built-in)',
        'Anti-Rug Bot (built-in)',
        'Leaderboards (built-in)',
        'Bubblemaps (built-in)'
      ]
    },
    {
      name: 'TIER 2',
      amount: '79,000',
      color: 'from-[#24c35b] to-[#1eaa4e]',
      icon: <Layers className="w-8 h-8" />,
      features: [
        'Everything in TIER 1',
        'Sniper Bot',
        'Rule-Based Bot',
        'X-Tracker',
        'Copy Trader',
        'Custom Alerts',
        'Multi-DEX Routes',
        'Copy Trade Pools',
        'Priority Stop'
      ]
    },
    {
      name: 'ELITE',
      amount: '150,000',
      color: 'from-[#24c35b] to-[#1eaa4e]',
      icon: <Crown className="w-8 h-8" />,
      features: [
        'Everything in TIER 2',
        'EKKO Elite',
        'Private Nodes',
        'Liquidity Sniper',
        'xWallet Trading',
        'Auto Copy Trader',
        'Premium Feed',
        'Enhanced Alerts'
      ]
    }
  ];

  const benefits = [
    { 
      title: 'Zero Trading Fees', 
      description: '$EKKO holders enjoy zero fees on all platform transactions',
      icon: <DollarSign className="w-6 h-6" />,
      color: 'from-[#24c35b] to-[#1eaa4e]'
    },
    { 
      title: 'Priority Execution', 
      description: 'Get priority order execution during high-volume periods',
      icon: <TrendingUp className="w-6 h-6" />,
      color: 'from-[#24c35b] to-[#1eaa4e]'
    },
    { 
      title: 'Governance Rights', 
      description: 'Vote on protocol upgrades and parameter changes',
      icon: <Shield className="w-6 h-6" />,
      color: 'from-[#24c35b] to-[#1eaa4e]'
    },
    { 
      title: 'Advanced Tools', 
      description: 'Access to premium trading tools and analytics',
      icon: <Database className="w-6 h-6" />,
      color: 'from-[#24c35b] to-[#1eaa4e]'
    }
  ];

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      {/* Architectural Lines */}
      <div className="fixed inset-0 pointer-events-none z-5">
        {/* Horizontal lines */}
        <div className="absolute top-32 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
        <div className="absolute top-1/2 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[#24c35b] to-transparent"></div>
        <div className="absolute bottom-32 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
        
        {/* Vertical lines */}
        <div className="absolute top-0 left-1/4 w-0.5 h-full bg-gradient-to-b from-transparent via-gray-200 to-transparent"></div>
        <div className="absolute top-0 right-1/4 w-0.5 h-full bg-gradient-to-b from-transparent via-gray-200 to-transparent"></div>
        
        {/* Cross decorations */}
        <div className="absolute top-32 left-1/4 w-8 h-8 transform -translate-x-1/2 -translate-y-1/2">
          <div className="absolute inset-0 bg-[#24c35b] w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
          <div className="absolute inset-0 bg-[#24c35b] h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
        </div>
        <div className="absolute top-32 right-1/4 w-8 h-8 transform translate-x-1/2 -translate-y-1/2">
          <div className="absolute inset-0 bg-[#24c35b] w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
          <div className="absolute inset-0 bg-[#24c35b] h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
        </div>

        {/* Corner plus decorations */}
        <div className="absolute top-8 left-8 text-[#24c35b] text-2xl font-bold opacity-60">+</div>
        <div className="absolute top-8 right-8 text-[#24c35b] text-2xl font-bold opacity-60">+</div>
        <div className="absolute bottom-8 left-8 text-[#24c35b] text-2xl font-bold opacity-60">+</div>
        <div className="absolute bottom-8 right-8 text-[#24c35b] text-2xl font-bold opacity-60">+</div>
      </div>

      {/* Large Background Text */}
      <div className="fixed inset-0 pointer-events-none select-none overflow-hidden z-0 flex flex-col items-center justify-center">
        <div className="text-[12rem] font-black text-gray-300/20 leading-none text-center">
          TOKEN
        </div>
        <div className="text-[12rem] font-black text-gray-300/20 leading-none text-center">
          ECONOMICS
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col z-10">
        {/* Top Content */}
        <div className="flex-1 flex flex-col items-center justify-center pt-24 pb-16 relative z-20">
          {/* EKKO Logo */}
          <div className="flex justify-center mb-8">
            <img 
              src={EKKO_LOGO}
              alt="EKKO Logo" 
              className="h-32 md:h-40 w-auto drop-shadow-lg"
            />
          </div>
          
          {/* Main Title */}
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight text-center">
            <span className="text-gradient block -mb-2">
              $EKKO
            </span>
            <span className="text-gray-900 text-3xl md:text-4xl lg:text-5xl">
              TOKENOMICS
            </span>
          </h1>
          
          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-gray-600 font-light leading-relaxed mb-8 max-w-4xl mx-auto text-center px-4">
            The native utility token powering the entire EKKO v2 ecosystem, designed to align incentives across users, liquidity providers, and the platform.
          </p>

          {/* Contract Address Card */}
          <div className="relative arch-card rounded-2xl p-6 mb-8 max-w-2xl w-full mx-auto">
            {/* Architectural corners */}
            <div className="absolute top-0 left-0 w-6 h-6">
              <div className="absolute top-0 left-0 w-full h-0.5 bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
              <div className="absolute top-0 left-0 w-0.5 h-full bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
            </div>
            <div className="absolute top-0 right-0 w-6 h-6">
              <div className="absolute top-0 right-0 w-full h-0.5 bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
              <div className="absolute top-0 right-0 w-0.5 h-full bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
            </div>
            
            <div className="text-center">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center justify-center gap-2">
                <Wallet className="w-5 h-5 text-[#24c35b]" />
                Token Contract Address
              </h3>
              <div className="bg-gray-100 rounded-xl p-4 font-mono text-sm break-all">
                {CONTRACT_ADDRESS}
              </div>
              <button 
                onClick={copyContract}
                className={`mt-4 px-6 py-2 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2 mx-auto ${
                  copiedContract 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-[#24c35b] text-white hover:bg-[#1eaa4e]'
                }`}
              >
                {copiedContract ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copiedContract ? 'Copied!' : 'Copy Address'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="relative z-10 bg-white border-t border-gray-200">
        <div className="container mx-auto max-w-7xl px-4">
          <div className="flex justify-center">
            <div className="flex space-x-1 bg-gray-100 p-1 rounded-xl">
              {[
                { id: 'overview', label: 'Token Overview', icon: <Coins className="w-4 h-4" /> },
                { id: 'distribution', label: 'Distribution', icon: <PieChart className="w-4 h-4" /> },
                { id: 'tiers', label: 'Access Tiers', icon: <Star className="w-4 h-4" /> },
                { id: 'benefits', label: 'Benefits', icon: <Zap className="w-4 h-4" /> }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'bg-[#24c35b] text-white shadow-lg'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="relative py-16 z-10">
        <div className="container mx-auto max-w-7xl px-4">

          {/* Token Overview */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                { label: 'Token Name', value: 'EKKO', icon: <Coins className="w-6 h-6" /> },
                { label: 'Token Symbol', value: '$EKKO', icon: <Star className="w-6 h-6" /> },
                { label: 'Blockchain', value: 'Solana', icon: <Globe className="w-6 h-6" /> },
                { label: 'Token Standard', value: 'SPL', icon: <Shield className="w-6 h-6" /> },
                { label: 'Total Supply', value: '1,000,000,000 EKKO', icon: <TrendingUp className="w-6 h-6" /> },
                { label: 'Vesting Period', value: '2 months', icon: <Lock className="w-6 h-6" /> }
              ].map((item, index) => (
                <div key={index} className="relative arch-card rounded-2xl p-6 text-center hover:shadow-2xl transition-all duration-300">
                  {/* Architectural corners */}
                  <div className="absolute top-0 left-0 w-4 h-4">
                    <div className="absolute top-0 left-0 w-full h-0.5 bg-[#24c35b]"></div>
                    <div className="absolute top-0 left-0 w-0.5 h-full bg-[#24c35b]"></div>
                  </div>
                  <div className="absolute top-0 right-0 w-4 h-4">
                    <div className="absolute top-0 right-0 w-full h-0.5 bg-[#24c35b]"></div>
                    <div className="absolute top-0 right-0 w-0.5 h-full bg-[#24c35b]"></div>
                  </div>
                  
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#24c35b] to-emerald-400 flex items-center justify-center text-white shadow-lg">
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.label}</h3>
                  <p className="text-2xl font-black text-[#24c35b]">{item.value}</p>
                </div>
              ))}
            </div>
          )}

          {/* Token Distribution */}
          {activeTab === 'distribution' && (
            <div className="animate-fadeIn">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-black text-gray-900 mb-4">Token Distribution</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Transparent and community-focused allocation ensuring sustainable growth and decentralization.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Visual Distribution */}
                <div className="relative">
                  <div className="w-80 h-80 mx-auto relative">
                    {/* Pie chart representation */}
                    <div className="w-full h-full rounded-full relative overflow-hidden shadow-2xl">
                      <div className="absolute inset-0 bg-gradient-to-br from-[#24c35b] to-emerald-400"></div>
                      <div className="absolute top-0 right-0 w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-bl-full"></div>
                      <div className="absolute top-0 right-8 w-4 h-4 bg-gradient-to-br from-purple-500 to-violet-400"></div>
                      <div className="absolute top-4 right-0 w-2 h-2 bg-gradient-to-br from-amber-500 to-orange-400 rounded-full"></div>
                    </div>
                    
                    {/* Center circle */}
                    <div className="absolute inset-8 bg-white rounded-full shadow-xl flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-4xl font-black text-gray-900">$EKKO</div>
                        <div className="text-sm text-gray-600 font-medium">Total Supply</div>
                        <div className="text-lg font-bold text-[#24c35b]">1B Tokens</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Distribution Details */}
                <div className="space-y-6">
                  {tokenDistribution.map((item, index) => (
                    <div key={index} className="relative arch-card rounded-xl p-6">
                      {/* Architectural corner */}
                      <div className="absolute top-0 left-0 w-4 h-4">
                        <div className="absolute top-0 left-0 w-full h-0.5 bg-[#24c35b]"></div>
                        <div className="absolute top-0 left-0 w-0.5 h-full bg-[#24c35b]"></div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center text-white shadow-lg`}>
                            {item.icon}
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">{item.label}</h3>
                            <p className="text-sm text-gray-600">{item.percentage === 96.5 ? 'Public ownership' : 'Team allocation'}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-black text-gray-900">{item.percentage}%</div>
                          <div className="text-sm text-gray-600">{(1000000000 * item.percentage / 100).toLocaleString()} tokens</div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-6">
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> Development, marketing, and team allocations are subject to a 2-month vesting period.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Access Tiers */}
          {activeTab === 'tiers' && (
            <div className="animate-fadeIn">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-black text-gray-900 mb-4">Access Tiers</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Three powerful tiers offering enhanced features and capabilities for every type of trader.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {accessTiers.map((tier, index) => (
                  <div key={index} className="relative arch-card rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 group">
                    {/* Architectural corners */}
                    <div className="absolute top-0 left-0 w-6 h-6">
                      <div className="absolute top-0 left-0 w-full h-0.5 bg-[#24c35b] shadow-lg"></div>
                      <div className="absolute top-0 left-0 w-0.5 h-full bg-[#24c35b] shadow-lg"></div>
                    </div>
                    <div className="absolute top-0 right-0 w-6 h-6">
                      <div className="absolute top-0 right-0 w-full h-0.5 bg-[#24c35b] shadow-lg"></div>
                      <div className="absolute top-0 right-0 w-0.5 h-full bg-[#24c35b] shadow-lg"></div>
                    </div>

                    {/* Header */}
                    <div className="text-center mb-8">
                      <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${tier.color} flex items-center justify-center text-white shadow-xl group-hover:scale-110 transition-transform`}>
                        {tier.icon}
                      </div>
                      <h3 className="text-2xl font-black text-gray-900 mb-2">{tier.name}</h3>
                      <div className="text-3xl font-black text-[#24c35b] mb-1">{tier.amount}</div>
                      <div className="text-sm text-gray-600 font-medium">$EKKO per month</div>
                    </div>

                    {/* Features */}
                    <div className="space-y-3">
                      {tier.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center gap-3">
                          <CheckCircle className="w-4 h-4 text-[#24c35b] flex-shrink-0" />
                          <span className="text-gray-700 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* Action Button */}
                    <button 
                      onClick={onLaunchPlatform}
                      className={`w-full mt-8 py-3 rounded-xl font-bold transition-all duration-300 flex items-center justify-center gap-2 group-hover:scale-105 bg-gradient-to-r ${tier.color} text-white shadow-lg hover:shadow-xl`}
                    >
                      <span>Get Started</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="text-center mt-12">
                <div className="bg-gray-50 rounded-xl p-6 max-w-2xl mx-auto">
                  <p className="text-gray-700">
                    <strong>All tiers include:</strong> Platform access and 24/7 community support
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Benefits */}
          {activeTab === 'benefits' && (
            <div className="animate-fadeIn">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-black text-gray-900 mb-4">Token Benefits</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Holding $EKKO tokens unlocks exclusive benefits and enhanced trading capabilities.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                {benefits.map((benefit, index) => (
                  <div key={index} className="relative arch-card rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 group">
                    {/* Architectural corners */}
                    <div className="absolute top-0 left-0 w-6 h-6">
                      <div className="absolute top-0 left-0 w-full h-0.5 bg-[#24c35b] shadow-lg"></div>
                      <div className="absolute top-0 left-0 w-0.5 h-full bg-[#24c35b] shadow-lg"></div>
                    </div>
                    
                    <div className="flex items-start gap-6">
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${benefit.color} flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform flex-shrink-0`}>
                        {benefit.icon}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Governance Section */}
              <div className="relative arch-card rounded-2xl p-8 bg-gradient-to-br from-[#24c35b]/5 to-emerald-50">
                {/* Architectural corners */}
                <div className="absolute top-0 left-0 w-6 h-6">
                  <div className="absolute top-0 left-0 w-full h-0.5 bg-[#24c35b] shadow-lg"></div>
                  <div className="absolute top-0 left-0 w-0.5 h-full bg-[#24c35b] shadow-lg"></div>
                </div>
                <div className="absolute top-0 right-0 w-6 h-6">
                  <div className="absolute top-0 right-0 w-full h-0.5 bg-[#24c35b] shadow-lg"></div>
                  <div className="absolute top-0 right-0 w-0.5 h-full bg-[#24c35b] shadow-lg"></div>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#24c35b] to-emerald-400 flex items-center justify-center text-white shadow-xl">
                    <Shield className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 mb-4">Governance Participation</h3>
                  <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                    EKKO token holders can participate in governance decisions through voting on protocol upgrades, parameter changes, and treasury allocations.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                    <div className="text-center">
                      <div className="text-2xl font-black text-[#24c35b]">1 EKKO = 1 VOTE</div>
                      <div className="text-sm text-gray-600 mt-1">(25,000 minimum)</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-gray-900">Vote Delegation</div>
                      <div className="text-sm text-gray-600 mt-1">Available</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-gray-900">Transparent</div>
                      <div className="text-sm text-gray-600 mt-1">Proposal System</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 z-10 bg-gray-50">
        <div className="container mx-auto max-w-7xl px-4">
          <div className="relative arch-card rounded-3xl p-12 text-center overflow-hidden bg-white">
            {/* Architectural corners */}
            <div className="absolute top-0 left-0 w-8 h-8">
              <div className="absolute top-0 left-0 w-full h-1 bg-[#24c35b] shadow-lg"></div>
              <div className="absolute top-0 left-0 w-1 h-full bg-[#24c35b] shadow-lg"></div>
            </div>
            <div className="absolute top-0 right-0 w-8 h-8">
              <div className="absolute top-0 right-0 w-full h-1 bg-[#24c35b] shadow-lg"></div>
              <div className="absolute top-0 right-0 w-1 h-full bg-[#24c35b] shadow-lg"></div>
            </div>

            <div className="max-w-3xl mx-auto relative z-10">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#24c35b] to-emerald-400 flex items-center justify-center text-white shadow-xl">
                <Coins className="h-8 w-8" />
              </div>
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
                Start Trading with <span className="text-gradient">$EKKO</span>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                Join thousands of traders already using EKKO's advanced platform for superior trading performance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={onLaunchPlatform}
                  className="btn-primary px-8 py-4 text-lg font-bold rounded-xl shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 group"
                >
                  <span>Launch Platform</span>
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <a
                  href="https://t.me/ekkoplatform"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 text-lg font-bold bg-white border-2 border-gray-300 text-gray-700 rounded-xl hover:border-[#24c35b] hover:text-[#24c35b] transition-all duration-300 flex items-center justify-center gap-3 group"
                >
                  <Users className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  <span>Join Community</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style jsx>{`
        .text-gradient {
          background: linear-gradient(135deg, #24c35b 0%, #1eaa4e 50%, #24c35b 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .arch-card {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        }

        .btn-primary {
          background: linear-gradient(135deg, #24c35b 0%, #1eaa4e 100%);
          color: white;
          border: none;
        }

        .btn-primary:hover {
          background: linear-gradient(135deg, #1eaa4e 0%, #24c35b 100%);
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .animate-fadeIn {
          animation: fadeIn 0.6s ease-out;
        }
      `}</style>
    </div>
  );
} 